import { useCallback, useContext, useEffect, useRef, useState } from "react";
import useScreenDimensions from "../hooks/useScreenDimensions";
import { CampaignModalContext, DashboardContext } from "../Pages/dashboard";
import * as motion from "motion/react-client";
import { AnimatePresence } from "motion/react";
import { EditableTitle } from "./EditableTitle";
import { createOnChangeHandler } from "../lib/helpers";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  SortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DragAndDropQuestion } from "./DragAndDropQuestion";
import NumberCircle from "./NumberCirlcle";
import { StandardButton } from "./standardButton";
import { datasource } from "../datasource/datasource";
import useOrganziations from "../hooks/useOrganizations";
import { useAuth } from "@clerk/clerk-react";
import useCampaigns from "../hooks/useCampaigns";
import { isMobile } from "react-device-detect";
import { ShepherdTourContext } from "../lib/shepherd";
import { useShepherd } from "../hooks/useShepherd";

const createQuestionObject = (text: string) => {return {text, uniqueId: Math.random()}};

export const CampaingModal = () => {
  const { getToken } = useAuth();
  const { width, height } = useScreenDimensions();
  const { isOpen, closeModal, campaignToEdit } =
    useContext(CampaignModalContext);
  const { openSnackBar } = useContext(DashboardContext);
  const {shepherd, onFocus, onBlur} = useShepherd();


  const updateCampaign = useCampaigns((state) => state.updateCampaign);
  const org = useOrganziations((state) => state.activeOrganization);

  const multiQuestionRef = useRef<HTMLInputElement>(null);

  const [campaignName, setCampaignName] = useState<string>(
    campaignToEdit?.name ?? "New Campaign"
  );
  const [items, setItems] = useState<{text: string, uniqueId: number}[]>(
    campaignToEdit?.questions.map((el) => createQuestionObject(el.text)) ?? []
  );

  const [clearItems, setClearItems] = useState<number>(0);

  const [link, setLink] = useState<string | null>(null);

  useEffect(() => {
    if(!isOpen) {
      setLink(null)
    }
  }, [isOpen]);

  const onTitleChange = createOnChangeHandler(setCampaignName);

  const cancelEvent = (e: any) => e.stopPropagation();

  useEffect(() => {
    setCampaignName(campaignToEdit?.name ?? "New Campaign");
    setItems(campaignToEdit?.questions.map((el) => createQuestionObject(el.text)) ?? []);
  }, [campaignToEdit, clearItems]);

  // Set up sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Start dragging after moving 5px
      },
    })
  );

  const updateQuestionText = useCallback((index: number, newText: string) => {
    const dub = [...items];
    dub.splice(index, 1, createQuestionObject(newText));
    setItems(dub);
  }, [items])

  // Handle drag end
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((prevItems) => {
        const oldIndex = prevItems.findIndex((el) => el.uniqueId === active.id);
        const newIndex = prevItems.findIndex((el) => el.uniqueId === over.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const handleDelete = (index: number) => () => {
    const dup = [...items];
    dup.splice(index, 1);
    setItems(dup);
  };

  const [newQuestion, setNewQuestion] = useState<string>("");

  const onNewQuestionChange = createOnChangeHandler(setNewQuestion);
  const wrappedOnBlur = useCallback(() => {
    onBlur();
    if (newQuestion.length) {
      setItems((prev) => [...prev, createQuestionObject(newQuestion)]);
      setNewQuestion("");
    }
    
  }, [setNewQuestion, newQuestion, onBlur]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSaveChanges = useCallback(async () => {
    setIsLoading(true);
    const token = await getToken();
    const campaign = await datasource.editCampaign(
      campaignName,
      items.map(el => el.text),
      org?.id as number,
      campaignToEdit?.id as number,
      token as string
    );
    updateCampaign(campaign);
    closeModal();
    setIsLoading(false);
    if (shepherd.isActive() && shepherd.currentStep?.id === 'Save Campaign') {
      shepherd.show('Activate General');
    }
  }, [getToken, campaignToEdit, org, campaignName, items]);

  const createCampaign = useCallback(async () => {
    setIsLoading(true);
    const token = await getToken();
    const campaign = await datasource.addNewCampaign(
      campaignName,
      items.map(el => el.text),
      org?.id as number,
      token as string
    );
    updateCampaign(campaign);
    setLink(window.location.host + `/c/${campaign.hash}`);
    setIsLoading(false);
    setClearItems((prev) => prev + 1);
  }, [getToken, items, org, campaignName]);

  const handleCopy = useCallback(async () => {
    try {
      if (!link) return;
      await navigator.clipboard.writeText(link);
      closeModal();
      openSnackBar();
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, [link]);

  useEffect(() => {
    if(isOpen && shepherd.isActive() && shepherd.currentStep?.id === 'Click to edit questions') {
      shepherd.next();
    }
  }, [isOpen])

  const safeClose = useCallback(() => {
    if (shepherd.isActive()) {
      return;
    } else {
      closeModal();
    }
  }, [shepherd, closeModal])

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key={"campaignModal"}
          style={{
            width,
            height,
            backgroundColor: "rgba(0, 0, 0, .3)",
            position: "fixed",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: 0,
            left: 0,
          }}
          onClick={safeClose}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          exit={{ opacity: 0 }}
        >
          <div
            onClick={cancelEvent}
            onTouchMove={cancelEvent}
            onTouchStart={cancelEvent}
            style={{
              width: "75%",
              minWidth: 400,
              backgroundColor: "white",
              padding: 24,
              borderRadius: 16,
              opacity: 1,
              touchAction: "none"
            }}
            className="shepherdCampaignModal"
          >
            {!link ? (
              <>
                <div style={{ marginBottom: "4rem" }}>
                  <div>
                    <EditableTitle
                      text={campaignName}
                      onUpdate={onTitleChange}
                      style={{
                        fontSize: 24,
                        fontWeight: 500,
                        marginBottom: "1rem",
                      }}
                    />
                  </div>
                  <div>
                    <span style={{ color: "#BCBCBC", fontSize: 14 }}>
                      {campaignToEdit
                        ? "Edit your campaign"
                        : "Create new Campaign and put as many questions as you would like"}
                    </span>
                  </div>
                </div>
                <hr />
                <div style={{ marginTop: "4rem" }}>
                  <div style={{ marginBottom: "1rem" }}>
                    <span>Questions</span>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={items.map(el => el.uniqueId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="campaignModalDragAndDrop">
                        {items.map((item, i) => (
                          <DragAndDropQuestion
                            updateQuestionText={updateQuestionText}
                            key={item.uniqueId}
                            id={item.uniqueId}
                            text={item.text}
                            questionNumber={i}
                            handleDelete={handleDelete}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "start",
                      marginBottom: "1rem",
                      alignItems: "center",
                    }}
                    className="campaignModalInput"
                  >
                    <NumberCircle
                      number={"+"}
                      color="black"
                      style={{ fontSize: "1.25rem" }}
                    />
                    <input
                      style={{
                        borderRadius: 24,
                        backgroundColor: "#F7F7F7",
                        paddingBottom: ".5rem",
                        paddingTop: ".5rem",
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                        marginLeft: ".5rem",
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                      ref={multiQuestionRef}
                      value={newQuestion}
                      onChange={onNewQuestionChange}
                      onFocus={onFocus}
                      onBlur={wrappedOnBlur}
                      placeholder="Type Your Question Here"
                    ></input>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: isMobile ? 0 : "1rem",
                    flexDirection: isMobile ? 'column-reverse' : 'row'
                  }}
                >
                  <StandardButton
                    buttonText="Cancel"
                    onClick={safeClose}
                    buttonColor="#F7F7F7"
                    customStyles={{ color: "black", fontSize: "1.25rem" }}
                  />
                  <StandardButton
                    isLoading={isLoading}
                    buttonText={
                      campaignToEdit ? "Save Changes" : "Create Campaign"
                    }
                    onClick={campaignToEdit ? onSaveChanges : createCampaign}
                    customStyles={{ fontSize: "1.25rem" }}
                  />
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                    fontSize: "1.25rem",
                    fontWeight: 500,
                    marginBottom: "1rem",
                  }}
                >
                  Your Campaign is all set up
                </div>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                    fontSize: "1rem",
                    marginBottom: "1rem ",
                  }}
                >
                  Press the button to copy the link
                </div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCopy}
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: "600px",
                    margin: "0 auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "16px",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: "16px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#374151",
                        fontWeight: "500",
                        fontSize: "18px",
                        userSelect: "none",
                      }}
                    >
                      {link}
                    </span>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
